import { EntityType, Rights } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { ProductExtendedClient } from '@frontend/product/api';
import { ProductExtended } from '@frontend/product/types';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { ApplicationContainer } from '../../application-container/application-container';
import { ApplicationCreateButton } from '../../components/create-button';
import OverviewHeader from '../../header/overview-header';
import { CreateProductModal } from './create-product-modal/create-product-modal.component';
import { useProducts } from './products.controller';

export const Products = () => {
    const viewProps = useProducts();
    const { openPlatformDetail } = usePlatformModalHost();
    return (
        <ApplicationContainer>
            <OverviewHeader
                title={
                    <div className='flex gap-2'>
                        <span>{CommonMessage.OBJECTS.CATALOG.DEFAULT}</span>
                        <span>/</span>
                        <span>{CommonMessage.OBJECTS.PRODUCT.PLURAL}</span>
                    </div>
                }
                createButton={
                    <ApplicationCreateButton
                        modalId='create-product-modal'
                        renderDefaultModal={() => <CreateProductModal />}
                        modalOptions={{ type: ModalType.PANE, className: 'max-w-[600px]' }}
                        createPermissions={[{ item: EntityType.PRODUCT, source: EntityType.ACCOUNT, right: Rights.CREATE, uid: '?' }]}
                        entity={EntityType.PRODUCT}
                    />
                }
            />
            <ReactiveObjectList<ProductExtended>
                id='products-list'
                useStaticListId
                fetch={(arg) => ProductExtendedClient.fetchExtendedProducts({ ...arg, account_id: viewProps.currentAccount })}
                dispatch={viewProps.dispatch}
                onSelectElement={(obj) =>
                    openPlatformDetail({
                        entityType: EntityType.PRODUCT,
                        id: obj.id,
                        context: { application: Application.CATALOGUE, feature: Feature.PRODUCTS }
                    })
                }
                columns={{
                    columns: ['name', 'sku', 'ean', 'categories'],
                    headerOverwrite: new Map([
                        ['name', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                        ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                        ['ean', CommonMessage.OBJECTS.PRODUCT.EAN],
                        ['categories', CommonMessage.OBJECTS.CATEGORY.DEFAULT]
                    ]),
                    customCells: new Map([
                        [
                            'categories',
                            (obj) => {
                                if (obj.row.original.categories === null || obj.row.original.categories.length === 0) {
                                    return <td className={defaultCellClassName}>/</td>;
                                }
                                return (
                                    <td className={defaultCellClassName}>
                                        <div className='flex gap-2.5 truncate text-sm font-medium'>
                                            <PlatformObjectBadge
                                                entityType={EntityType.PRODUCT_CATEGORY}
                                                id={obj.row.original.categories[0].entity_id}
                                                display={obj.row.original.categories[0].name}
                                                foreign={obj.row.original.categories[0].foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.categories[0])}
                                            />
                                            {obj.row.original.categories.length > 1 && (
                                                <MultiEntityButton
                                                    entities={obj.row.original.categories.slice(1)}
                                                    render={(entity) => (
                                                        <PlatformObjectBadge
                                                            entityType={EntityType.PRODUCT_CATEGORY}
                                                            id={entity.entity_id}
                                                            display={entity.name}
                                                            foreign={entity.foreign}
                                                            accountId={accountIdFromResolvedEntity(entity)}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </div>
                                    </td>
                                );
                            }
                        ]
                    ])
                }}
                type={EntityType.PRODUCT}
                activeFilter={{
                    searchKeys: ['name', 'sku', 'ean'],
                    displayName: new Map([
                        ['name', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                        ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                        ['ean', CommonMessage.OBJECTS.PRODUCT.EAN]
                    ])
                }}
            />
        </ApplicationContainer>
    );
};
